import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {ImpersonationService} from '../../services/impersonation/impersonation';
import {DefaultButtonComponent} from '../default-button/default-button.component';

@Component({
  selector: 'app-impersonation-banner',
  standalone: true,
  styles: [':host { display: block; flex-shrink: 0; position: relative; z-index: 1100; }'],
  imports: [TranslatePipe, DefaultButtonComponent],
  template: `
    @if (impersonation.current(); as state) {
      <aside class="flex flex-wrap items-center justify-between gap-3 border-b border-primary-default bg-neutral-20 px-6 py-3 text-white" aria-label="Impersonation">
        <div>
          <p class="font-semibold">{{ 'impersonation.active' | translate: {name: state.user.first_name + ' ' + state.user.last_name, email: state.user.email} }}</p>
          <p>{{ 'impersonation.remaining' | translate: {time: remaining()} }}</p>
          @if (error()) { <p role="alert">{{ 'impersonation.stop_failed' | translate }}</p> }
          @if (extensionError()) { <p role="alert">{{ 'impersonation.extend_failed' | translate }}</p> }
        </div>
        <div class="flex flex-wrap items-center gap-2">
          @if (canExtend()) {
            <app-default-button translateKey="impersonation.extend" size="small" fillType="outline"
              [disabled]="stopping() || extending()" [loadingSpinner]="extending()" (clickEmit)="extendImpersonation()" />
          }
          <app-default-button translateKey="impersonation.stop" size="small" [disabled]="stopping() || extending()" [loadingSpinner]="stopping()" (clickEmit)="stopImpersonation()" />
        </div>
      </aside>
    }
  `,
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  protected readonly impersonation = inject(ImpersonationService);
  private readonly now = signal(Date.now());
  private readonly secondsRemaining = computed(() => {
    const state = this.impersonation.current();
    return state ? Math.max(0, Math.ceil((Date.parse(state.impersonation.expires_at) - this.now()) / 1000)) : 0;
  });
  protected readonly remaining = computed(() => {
    const seconds = this.secondsRemaining();
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  });
  protected readonly stopping = signal(false);
  protected readonly error = signal(false);
  protected readonly canExtend = computed(() => this.secondsRemaining() > 0 && this.secondsRemaining() <= 300);
  protected readonly extending = signal(false);
  protected readonly extensionError = signal(false);
  private timer?: ReturnType<typeof setInterval>;
  private expirationAttempted = false;
  private expiration: string | null = null;

  ngOnInit(): void {
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  ngOnDestroy(): void { clearInterval(this.timer); }

  protected async stopImpersonation(): Promise<void> {
    if (this.stopping()) return;
    this.stopping.set(true);
    this.error.set(false);
    try { await this.impersonation.stop(); }
    catch { this.error.set(true); }
    finally { this.stopping.set(false); }
  }

  protected async extendImpersonation(): Promise<void> {
    if (!this.canExtend() || this.extending() || this.stopping()) return;
    this.extending.set(true);
    this.extensionError.set(false);
    try { await this.impersonation.extend(); }
    catch { this.extensionError.set(true); }
    finally { this.extending.set(false); this.tick(); }
  }

  private async expireImpersonation(): Promise<void> {
    this.stopping.set(true);
    this.error.set(false);
    try { await this.impersonation.stopIfExpired(); }
    catch { this.error.set(true); }
    finally { this.stopping.set(false); this.tick(); }
  }

  private tick(): void {
    this.now.set(Date.now());
    const state = this.impersonation.current();
    if (!state) return;
    if (state.impersonation.expires_at !== this.expiration) {
      this.expiration = state.impersonation.expires_at;
      this.expirationAttempted = false;
    }
    const seconds = this.secondsRemaining();
    if (seconds === 0 && !this.expirationAttempted && !this.extending() && !this.stopping()) {
      this.expirationAttempted = true;
      void this.expireImpersonation();
    }
  }
}
